import apiClient from "@/lib/api";
import { redact } from "./redact";

export type ReportSource =
  | "window"
  | "unhandledrejection"
  | "react_error_boundary"
  | "react_global_error"
  | "interview_submit_answer"
  // 回答送信の応答を取り逃したが、進行状況を取り直して面接を続けられた事象（#1825）。
  // 回答は保存されており候補者は詰まっていないため、送信の失敗とは別種別にして
  // バックエンド側で通知の水準を下げる。
  | "interview_submit_answer_recovered"
  | "audio_analysis_unavailable"
  | "interview_recording_stalled"
  // 企業向け一次面接で深掘りが行えなかった事象（#1872）。文字起こしの接続・判定の失敗は
  // 受験を止めずに深掘りなしで進むため、応募者には見えない。障害に気づくためにここで記録する。
  | "interview_follow_up_unavailable"
  // 応募書類の原本を描画できず、読み取り本文へ退避した事象（#1866）。
  // 画面には退避したことしか出ないため、原因を追えるようにここで記録する。
  | "document_review_preview_unavailable"
  // 録音停止時に録音データが空だった、または録音中に MediaRecorder が異常を起こした
  // 事象（#1842）。画面で案内済みのため、バックエンド側で通知の水準を下げる。
  // 原因の端末・ブラウザを追跡するために記録する。
  | "audio_recording_failed";

export interface ReportInput {
  error: unknown;
  source: ReportSource;
  url?: string;
  digest?: string;
}

interface ReportPayload {
  message: string;
  stack?: string;
  url?: string;
  user_agent?: string;
  error_name: string;
  source: ReportSource;
  digest?: string;
}

const DEDUPE_WINDOW_MS = 60_000;
const DEDUPE_MAX_KEYS = 100;
const MAX_MESSAGE_LEN = 500;
const MAX_STACK_LEN = 4000;

// 実害のない既知のブラウザ警告・追跡不能なエラーは報告しない（先頭一致で判定）。
// ResizeObserver 警告はブラウザにより2表現あり、HubSpot 埋め込み等の
// 外部コード由来で発生する（#1278）。error_name / source では本物の
// エラーと区別できないため、メッセージ内容でのみ判定する。
// Script error. はクロスオリジンスクリプトで発生したエラーの匿名化表現で、
// メッセージ・スタックとも取得できず原因追跡ができない（#1305）。
const IGNORED_MESSAGE_PREFIXES = [
  "ResizeObserver loop completed with undelivered notifications",
  "ResizeObserver loop limit exceeded",
  "Script error.",
];

const dedupeMap = new Map<string, number>();

function sanitizeUrl(raw: string | undefined): string | undefined {
  if (!raw) return undefined;
  try {
    const u = new URL(raw);
    u.search = "";
    u.hash = "";
    // パスに受験用の符号が乗る画面があるため、パスにも伏せ字を適用する（#1767）。
    return redact(u.origin + u.pathname);
  } catch {
    return undefined;
  }
}

function shouldSkipForDedupe(key: string): boolean {
  const now = Date.now();
  const last = dedupeMap.get(key);
  if (last !== undefined && now - last < DEDUPE_WINDOW_MS) {
    return true;
  }
  if (dedupeMap.size >= DEDUPE_MAX_KEYS) {
    const oldest = dedupeMap.keys().next().value;
    if (oldest !== undefined) {
      dedupeMap.delete(oldest);
    }
  }
  dedupeMap.set(key, now);
  return false;
}

export async function reportError(input: ReportInput): Promise<void> {
  let message: string;
  let stack: string | undefined;
  let errorName: string;
  const err = input.error;
  if (err instanceof Error) {
    message = err.message ?? "";
    stack = err.stack;
    errorName = err.name || "Error";
  } else {
    message = String(err);
    stack = undefined;
    errorName = "NonError";
  }

  if (IGNORED_MESSAGE_PREFIXES.some((prefix) => message.startsWith(prefix))) {
    return;
  }

  message = redact(message).slice(0, MAX_MESSAGE_LEN);
  if (stack) {
    stack = redact(stack).slice(0, MAX_STACK_LEN);
  }

  const rawUrl =
    input.url ??
    (typeof window !== "undefined" ? window.location.href : undefined);
  const url = sanitizeUrl(rawUrl);
  const userAgent =
    typeof navigator !== "undefined" ? navigator.userAgent : undefined;

  const payload: ReportPayload = {
    message,
    stack,
    url,
    user_agent: userAgent,
    error_name: errorName,
    source: input.source,
    digest: input.digest,
  };

  const dedupeKey = `${input.source}:${message.slice(0, 200)}`;
  if (shouldSkipForDedupe(dedupeKey)) {
    return;
  }

  try {
    if (
      input.source === "react_global_error" ||
      input.source === "interview_submit_answer" ||
      input.source === "interview_submit_answer_recovered"
    ) {
      const apiUrl =
        process.env.NEXT_PUBLIC_API_URL || "http://localhost:3001/api/v1";
      await fetch(`${apiUrl}/client_errors`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      }).catch(() => {});
    } else {
      await apiClient.post("/client_errors", payload).catch(() => {});
    }
  } catch {
    // silent fail
  }
}

export function __resetReportErrorForTest(): void {
  dedupeMap.clear();
}
